import { useState, useMemo, useCallback } from 'react';
import { GlobalButton } from './GlobalButton';
import GlobalEmptyState from './GlobalEmptyState';
import GlobalSidebar from './GlobalSidebar';
import { GlobalCheckbox } from './GlobalCheckbox';
import { ViewSectionHeader } from './ViewPage';
import { ProjectPhaseForm, type ProjectPhaseFormValues } from './ProjectPhaseForm';
import { ProjectTaskForm, type ProjectTaskFormValues } from './ProjectTaskForm';
import { ProjectTextImportSidebar } from './ProjectTextImportSidebar';
import { useTheme } from '../context/ThemeProvider';
import { formatDateShort } from '../utils/formatters';
import { newId } from '../utils/id';
import type {
  RealEstateProjectPhase,
  RealEstateProjectTask,
  ProjectPhaseStatus,
} from '../types';
import styles from './ProjectGanttView.module.css';

const STATUS_LABELS: Record<ProjectPhaseStatus, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  blocked: 'Blocked',
};

const STATUS_COLORS: Record<ProjectPhaseStatus, string> = {
  pending: '#888',
  in_progress: '#3b82f6',
  completed: '#22c55e',
  blocked: '#ef4444',
};

type Props = {
  phases: RealEstateProjectPhase[];
  tasks: RealEstateProjectTask[];
  saving?: boolean;
  error?: string | null;
  onSavePhase: (phase: RealEstateProjectPhase) => Promise<void>;
  onDeletePhase: (phaseId: string) => Promise<void>;
  onSaveTask: (task: RealEstateProjectTask) => Promise<void>;
  onDeleteTask: (taskId: string) => Promise<void>;
  onToggleTask: (taskId: string) => void;
  onBulkImport?: (phases: RealEstateProjectPhase[], tasks: RealEstateProjectTask[]) => Promise<void>;
};

function getPhaseProgressPercent(
  phase: RealEstateProjectPhase,
  tasks: RealEstateProjectTask[]
): number {
  const phaseTasks = tasks.filter((t) => t.phaseId === phase.id);
  if (phaseTasks.length === 0) return phase.status === 'completed' ? 100 : 0;
  const completed = phaseTasks.filter((t) => t.done).length;
  return Math.round((completed / phaseTasks.length) * 100);
}

function getTimelineRange(
  phases: RealEstateProjectPhase[]
): { startDate: Date; endDate: Date } | null {
  const dates: Date[] = [];
  for (const phase of phases) {
    if (phase.expectedStartDate) {
      dates.push(new Date(phase.expectedStartDate));
    }
    if (phase.expectedEndDate) {
      dates.push(new Date(phase.expectedEndDate));
    }
    if (phase.completedDate) {
      dates.push(new Date(phase.completedDate));
    }
  }
  if (dates.length === 0) return null;
  const min = new Date(Math.min(...dates.map((d) => d.getTime())));
  const max = new Date(Math.max(...dates.map((d) => d.getTime())));
  min.setDate(min.getDate() - 7);
  max.setDate(max.getDate() + 14);
  return { startDate: min, endDate: max };
}

function getBarPosition(
  phase: RealEstateProjectPhase,
  timelineStart: Date,
  timelineEnd: Date
): { left: string; width: string } | null {
  const start = phase.expectedStartDate ? new Date(phase.expectedStartDate) : null;
  const end = phase.expectedEndDate ? new Date(phase.expectedEndDate) : null;
  if (!start && !end) return null;

  const totalDays =
    (timelineEnd.getTime() - timelineStart.getTime()) / (1000 * 60 * 60 * 24);
  const startDay = start
    ? (start.getTime() - timelineStart.getTime()) / (1000 * 60 * 60 * 24)
    : 0;
  const endDay = end
    ? (end.getTime() - timelineStart.getTime()) / (1000 * 60 * 60 * 24)
    : startDay + 7;

  const left = Math.max(0, (startDay / totalDays) * 100);
  const right = Math.min(100, (endDay / totalDays) * 100);
  const width = Math.max(2, right - left);

  return { left: `${left}%`, width: `${width}%` };
}

function getMonthMarkers(
  startDate: Date,
  endDate: Date
): Array<{ label: string; position: number }> {
  const markers: Array<{ label: string; position: number }> = [];
  const totalDays =
    (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);

  const current = new Date(startDate);
  current.setDate(1);
  if (current < startDate) {
    current.setMonth(current.getMonth() + 1);
  }

  while (current <= endDate) {
    const dayOffset =
      (current.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
    const position = (dayOffset / totalDays) * 100;
    const label = current.toLocaleDateString('en-US', {
      month: 'short',
      year: '2-digit',
    });
    markers.push({ label, position });
    current.setMonth(current.getMonth() + 1);
  }

  return markers;
}

function getTodayPosition(
  startDate: Date,
  endDate: Date
): number | null {
  const today = new Date();
  if (today < startDate || today > endDate) return null;
  const totalDays =
    (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
  const dayOffset =
    (today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
  return (dayOffset / totalDays) * 100;
}

export function ProjectGanttView({
  phases,
  tasks,
  saving = false,
  error,
  onSavePhase,
  onDeletePhase,
  onSaveTask,
  onDeleteTask,
  onToggleTask,
  onBulkImport,
}: Props) {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const [phaseFormOpen, setPhaseFormOpen] = useState(false);
  const [editingPhase, setEditingPhase] = useState<RealEstateProjectPhase | null>(null);
  const [taskFormOpen, setTaskFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<RealEstateProjectTask | null>(null);
  const [taskPhaseId, setTaskPhaseId] = useState<string | null>(null);
  const [expandedPhases, setExpandedPhases] = useState<Set<string>>(new Set());
  const [deleteConfirmPhaseId, setDeleteConfirmPhaseId] = useState<string | null>(null);
  const [importSidebarOpen, setImportSidebarOpen] = useState(false);

  const sortedPhases = useMemo(
    () => [...phases].sort((a, b) => a.sortOrder - b.sortOrder),
    [phases]
  );

  const timelineRange = useMemo(() => getTimelineRange(phases), [phases]);
  const monthMarkers = useMemo(() => {
    if (!timelineRange) return [];
    return getMonthMarkers(timelineRange.startDate, timelineRange.endDate);
  }, [timelineRange]);

  const toggleExpanded = useCallback((phaseId: string) => {
    setExpandedPhases((prev) => {
      const next = new Set(prev);
      if (next.has(phaseId)) {
        next.delete(phaseId);
      } else {
        next.add(phaseId);
      }
      return next;
    });
  }, []);

  const handleAddPhase = useCallback(() => {
    setEditingPhase(null);
    setPhaseFormOpen(true);
  }, []);

  const handleEditPhase = useCallback((phase: RealEstateProjectPhase) => {
    setEditingPhase(phase);
    setPhaseFormOpen(true);
  }, []);

  const handlePhaseSubmit = useCallback(
    async (values: ProjectPhaseFormValues) => {
      const now = new Date().toISOString();
      const phase: RealEstateProjectPhase = {
        id: editingPhase?.id ?? newId(),
        name: values.name,
        status: values.status,
        dependsOn: values.dependsOn,
        expectedStartDate: values.expectedStartDate,
        expectedEndDate: values.expectedEndDate,
        completedDate: values.completedDate,
        responsibleParty: values.responsibleParty,
        notes: values.notes,
        sortOrder: editingPhase?.sortOrder ?? phases.length,
        createdAt: editingPhase?.createdAt ?? now,
        updatedAt: now,
      };
      await onSavePhase(phase);
      setPhaseFormOpen(false);
      setEditingPhase(null);
    },
    [editingPhase, phases.length, onSavePhase]
  );

  const handleDeletePhase = useCallback(
    async (phaseId: string) => {
      await onDeletePhase(phaseId);
      setDeleteConfirmPhaseId(null);
    },
    [onDeletePhase]
  );

  const handleAddTask = useCallback((phaseId: string) => {
    setTaskPhaseId(phaseId);
    setEditingTask(null);
    setTaskFormOpen(true);
  }, []);

  const handleEditTask = useCallback((task: RealEstateProjectTask) => {
    setTaskPhaseId(task.phaseId);
    setEditingTask(task);
    setTaskFormOpen(true);
  }, []);

  const handleTaskSubmit = useCallback(
    async (values: ProjectTaskFormValues) => {
      if (!taskPhaseId) return;
      const now = new Date().toISOString();
      const task: RealEstateProjectTask = {
        id: editingTask?.id ?? newId(),
        phaseId: taskPhaseId,
        title: values.title,
        done: editingTask?.done ?? false,
        dueDate: values.dueDate,
        assignee: values.assignee,
        notes: values.notes,
        addToGlobalTasks: values.addToGlobalTasks,
        createdAt: editingTask?.createdAt ?? now,
        completedAt: editingTask?.completedAt ?? null,
      };
      await onSaveTask(task);
      setTaskFormOpen(false);
      setEditingTask(null);
      setTaskPhaseId(null);
    },
    [editingTask, taskPhaseId, onSaveTask]
  );

  const handleDeleteTask = useCallback(
    async (taskId: string) => {
      await onDeleteTask(taskId);
    },
    [onDeleteTask]
  );

  const handleBulkImport = useCallback(
    async (newPhases: RealEstateProjectPhase[], newTasks: RealEstateProjectTask[]) => {
      if (onBulkImport) {
        await onBulkImport(newPhases, newTasks);
      }
      setImportSidebarOpen(false);
    },
    [onBulkImport]
  );

  const importIcon = (
    <img
      src={isDarkMode ? '/assets/icons/text.document.svg' : '/assets/icons/text.document.black.svg'}
      alt=""
      width={16}
      height={16}
    />
  );

  const plusIcon = (
    <img
      src={isDarkMode ? '/assets/icons/plus.svg' : '/assets/icons/plus.black.svg'}
      alt=""
      width={16}
      height={16}
    />
  );

  const pencilIcon = (
    <img
      src={isDarkMode ? '/assets/icons/pencil.svg' : '/assets/icons/pencil.black.svg'}
      alt=""
      width={14}
      height={14}
    />
  );

  const trashIcon = (
    <img
      src={isDarkMode ? '/assets/icons/trash.svg' : '/assets/icons/trash.black.svg'}
      alt=""
      width={14}
      height={14}
    />
  );

  if (phases.length === 0) {
    return (
      <>
        <ViewSectionHeader
          eyebrow="Project"
          title="Project timeline"
          subtitle="Track construction phases, permits, and milestones with a Gantt-like timeline."
          actions={
            <>
              {onBulkImport ? (
                <GlobalButton
                  type="button"
                  variant="secondary"
                  onClick={() => setImportSidebarOpen(true)}
                  icon={importIcon}
                >
                  Import from text
                </GlobalButton>
              ) : null}
              <GlobalButton
                type="button"
                variant="secondary"
                onClick={handleAddPhase}
                icon={plusIcon}
              >
                Add phase
              </GlobalButton>
            </>
          }
        />
        <GlobalEmptyState
          buttonText="Add first phase"
          onButtonClick={handleAddPhase}
        />

        <GlobalSidebar isOpen={phaseFormOpen} onClose={() => setPhaseFormOpen(false)}>
          <div className="sidebarFormStack">
            <h2 className="sidebarFormTitle">Add phase</h2>
            <p className="bankProviderConnectIntro">
              Create a new project phase with expected dates and dependencies.
            </p>
            <ProjectPhaseForm
              phases={phases}
              saving={saving}
              error={error}
              onSubmit={handlePhaseSubmit}
              onCancel={() => setPhaseFormOpen(false)}
            />
          </div>
        </GlobalSidebar>

        {onBulkImport ? (
          <GlobalSidebar isOpen={importSidebarOpen} onClose={() => setImportSidebarOpen(false)}>
            <ProjectTextImportSidebar
              existingPhases={phases}
              onImport={handleBulkImport}
              onCancel={() => setImportSidebarOpen(false)}
            />
          </GlobalSidebar>
        ) : null}
      </>
    );
  }

  return (
    <>
      <ViewSectionHeader
        eyebrow="Project"
        title="Project timeline"
        subtitle={`${phases.length} phase${phases.length !== 1 ? 's' : ''} · ${tasks.length} task${tasks.length !== 1 ? 's' : ''}`}
        actions={
          <>
            {onBulkImport ? (
              <GlobalButton
                type="button"
                variant="secondary"
                onClick={() => setImportSidebarOpen(true)}
                icon={importIcon}
              >
                Import from text
              </GlobalButton>
            ) : null}
            <GlobalButton
              type="button"
              variant="secondary"
              onClick={handleAddPhase}
              icon={plusIcon}
            >
              Add phase
            </GlobalButton>
          </>
        }
      />

      {timelineRange ? (
        <section className={styles.timelineWrapper}>
          <div className={styles.timelineScroll}>
            <div className={styles.timelineGrid}>
              {/* Header row with month labels */}
              <div className={styles.timelineHeader}>
                <div className={styles.labelHeader}>Phase</div>
                <div className={styles.monthLabelsTrack}>
                  {/* Vertical grid lines in header */}
                  <div className={styles.headerGridLines}>
                    {monthMarkers.map((marker, i) => (
                      <div
                        key={i}
                        className={styles.headerGridLine}
                        style={{ left: `${marker.position}%` }}
                      />
                    ))}
                  </div>
                  {/* Today marker - only in header */}
                  {(() => {
                    const todayPos = getTodayPosition(
                      timelineRange.startDate,
                      timelineRange.endDate
                    );
                    return todayPos !== null ? (
                      <div
                        className={styles.todayMarkerHeader}
                        style={{ left: `${todayPos}%` }}
                      >
                        <span className={styles.todayLabel}>Today</span>
                      </div>
                    ) : null;
                  })()}
                  {/* Month labels */}
                  {monthMarkers.map((marker, i) => (
                    <div
                      key={i}
                      className={styles.monthLabel}
                      style={{ left: `${marker.position}%` }}
                    >
                      {marker.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Body rows */}
              <div className={styles.timelineBody}>
                {sortedPhases.map((phase, rowIndex) => {
                  const barPos = getBarPosition(
                    phase,
                    timelineRange.startDate,
                    timelineRange.endDate
                  );
                  const todayPos = getTodayPosition(
                    timelineRange.startDate,
                    timelineRange.endDate
                  );
                  const isLastRow = rowIndex === sortedPhases.length - 1;
                  return (
                    <div
                      key={phase.id}
                      className={`${styles.timelineRow} ${isLastRow ? styles.timelineRowLast : ''}`}
                    >
                      <div className={styles.phaseLabel}>{phase.name}</div>
                      <div className={styles.timelineTrack}>
                        {/* Vertical grid lines */}
                        <div className={styles.monthGridLines}>
                          {monthMarkers.map((marker, i) => (
                            <div
                              key={i}
                              className={styles.monthGridLine}
                              style={{ left: `${marker.position}%` }}
                            />
                          ))}
                        </div>
                        {/* Today marker line (no label) */}
                        {todayPos !== null ? (
                          <div
                            className={styles.todayMarkerLine}
                            style={{ left: `${todayPos}%` }}
                          />
                        ) : null}
                        {/* Phase bar */}
                        {barPos ? (
                          <div
                            className={styles.timelineBar}
                            style={{
                              left: barPos.left,
                              width: barPos.width,
                              backgroundColor: STATUS_COLORS[phase.status],
                            }}
                            title={`${phase.name}: ${STATUS_LABELS[phase.status]}`}
                          >
                            <span className={styles.barLabel}>
                              {STATUS_LABELS[phase.status]}
                            </span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      <ViewSectionHeader
        eyebrow="Project"
        title="Phases"
        subtitle="Click a phase to expand tasks and details."
      />

      <section className={styles.phaseList}>
        {sortedPhases.map((phase) => {
          const phaseTasks = tasks.filter((t) => t.phaseId === phase.id);
          const isExpanded = expandedPhases.has(phase.id);
          const progress = getPhaseProgressPercent(phase, tasks);
          const dependencyNames = (phase.dependsOn ?? [])
            .map((depId) => phases.find((p) => p.id === depId)?.name)
            .filter(Boolean);

          return (
            <div key={phase.id} className={styles.phaseCard}>
              <button
                type="button"
                className={styles.phaseHeader}
                onClick={() => toggleExpanded(phase.id)}
                aria-expanded={isExpanded}
              >
                <span
                  className={styles.statusDot}
                  style={{ backgroundColor: STATUS_COLORS[phase.status] }}
                  aria-label={STATUS_LABELS[phase.status]}
                />
                <span className={styles.phaseName}>{phase.name}</span>
                <div className={styles.phaseMetaRow}>
                  <span className={styles.phaseStatus}>
                    {STATUS_LABELS[phase.status]}
                  </span>
                  {phase.expectedEndDate ? (
                    <span className={styles.phaseDue}>
                      Due {formatDateShort(phase.expectedEndDate)}
                    </span>
                  ) : null}
                </div>
                <span className={styles.phaseProgress}>{progress}%</span>
                <span
                  className={`${styles.expandIcon} ${isExpanded ? styles.expandIconOpen : ''}`}
                  aria-hidden
                >
                  ▼
                </span>
              </button>

              {isExpanded ? (
                <div className={styles.phaseBody}>
                  <div className={styles.phaseDetails}>
                    {phase.responsibleParty ? (
                      <div className={styles.phaseDetailItem}>
                        <span className={styles.phaseDetailLabel}>Responsible</span>
                        <span className={styles.phaseDetailValue}>{phase.responsibleParty}</span>
                      </div>
                    ) : null}
                    {phase.expectedStartDate ? (
                      <div className={styles.phaseDetailItem}>
                        <span className={styles.phaseDetailLabel}>Start date</span>
                        <span className={styles.phaseDetailValue}>{formatDateShort(phase.expectedStartDate)}</span>
                      </div>
                    ) : null}
                    {phase.expectedEndDate ? (
                      <div className={styles.phaseDetailItem}>
                        <span className={styles.phaseDetailLabel}>Expected end</span>
                        <span className={styles.phaseDetailValue}>{formatDateShort(phase.expectedEndDate)}</span>
                      </div>
                    ) : null}
                    {phase.completedDate ? (
                      <div className={styles.phaseDetailItem}>
                        <span className={styles.phaseDetailLabel}>Completed</span>
                        <span className={styles.phaseDetailValue}>{formatDateShort(phase.completedDate)}</span>
                      </div>
                    ) : null}
                    {dependencyNames.length > 0 ? (
                      <div className={styles.phaseDetailItem}>
                        <span className={styles.phaseDetailLabel}>Depends on</span>
                        <span className={styles.phaseDetailValue}>{dependencyNames.join(', ')}</span>
                      </div>
                    ) : null}
                    {phase.notes ? (
                      <div className={`${styles.phaseDetailItem} ${styles.phaseNotes}`}>
                        <span className={styles.phaseDetailLabel}>Notes</span>
                        <span className={styles.phaseDetailValue}>{phase.notes}</span>
                      </div>
                    ) : null}
                  </div>

                  <div className={styles.phaseActions}>
                    <GlobalButton
                      type="button"
                      variant="secondary"
                      autoWidth
                      onClick={() => handleEditPhase(phase)}
                      icon={pencilIcon}
                    >
                      Edit
                    </GlobalButton>
                    {deleteConfirmPhaseId === phase.id ? (
                      <>
                        <span className="muted" style={{ fontSize: '0.875rem' }}>
                          Delete phase and its tasks?
                        </span>
                        <GlobalButton
                          type="button"
                          variant="secondary"
                          destructive
                          autoWidth
                          isLoading={saving}
                          onClick={() => handleDeletePhase(phase.id)}
                        >
                          Confirm
                        </GlobalButton>
                        <GlobalButton
                          type="button"
                          variant="secondary"
                          autoWidth
                          onClick={() => setDeleteConfirmPhaseId(null)}
                        >
                          Cancel
                        </GlobalButton>
                      </>
                    ) : (
                      <GlobalButton
                        type="button"
                        variant="secondary"
                        destructive
                        autoWidth
                        onClick={() => setDeleteConfirmPhaseId(phase.id)}
                        icon={trashIcon}
                      >
                        Delete
                      </GlobalButton>
                    )}
                  </div>

                  <div className={styles.taskSection}>
                    <div className={styles.taskHeader}>
                      <span className={styles.taskTitle}>
                        Tasks ({phaseTasks.length})
                      </span>
                      <GlobalButton
                        type="button"
                        variant="secondary"
                        autoWidth
                        onClick={() => handleAddTask(phase.id)}
                        icon={plusIcon}
                      >
                        Add task
                      </GlobalButton>
                    </div>

                    {phaseTasks.length > 0 ? (
                      <ul className={styles.taskList}>
                        {phaseTasks.map((task) => (
                          <li key={task.id} className={styles.taskItem}>
                            <GlobalCheckbox
                              checked={task.done}
                              onChange={() => onToggleTask(task.id)}
                              label=""
                            />
                            <div className={styles.taskContent}>
                              <span>{task.title}</span>
                              {(task.dueDate || task.assignee || task.addToGlobalTasks) ? (
                                <div className={styles.taskMeta}>
                                  {task.dueDate ? (
                                    <span>Due {formatDateShort(task.dueDate)}</span>
                                  ) : null}
                                  {task.assignee ? (
                                    <span>{task.assignee}</span>
                                  ) : null}
                                  {task.addToGlobalTasks ? (
                                    <span className={styles.globalTaskBadge}>
                                      Global
                                    </span>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                            <div className={styles.taskActions}>
                              <button
                                type="button"
                                className={styles.iconButton}
                                onClick={() => handleEditTask(task)}
                                title="Edit task"
                              >
                                {pencilIcon}
                              </button>
                              <button
                                type="button"
                                className={styles.iconButton}
                                onClick={() => handleDeleteTask(task.id)}
                                title="Delete task"
                              >
                                {trashIcon}
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="muted" style={{ marginTop: 8 }}>
                        No tasks yet — add tasks to track work within this phase.
                      </p>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </section>

      <GlobalSidebar
        isOpen={phaseFormOpen}
        onClose={() => {
          if (!saving) {
            setPhaseFormOpen(false);
            setEditingPhase(null);
          }
        }}
      >
        <div className="sidebarFormStack">
          <h2 className="sidebarFormTitle">
            {editingPhase ? 'Edit phase' : 'Add phase'}
          </h2>
          <p className="bankProviderConnectIntro">
            {editingPhase
              ? 'Update the phase details, dates, and dependencies.'
              : 'Create a new project phase with expected dates and dependencies.'}
          </p>
          <ProjectPhaseForm
            phases={phases.filter((p) => p.id !== editingPhase?.id)}
            phase={editingPhase ?? undefined}
            saving={saving}
            error={error}
            submitLabel={editingPhase ? 'Save changes' : 'Add phase'}
            onSubmit={handlePhaseSubmit}
            onCancel={() => {
              setPhaseFormOpen(false);
              setEditingPhase(null);
            }}
          />
        </div>
      </GlobalSidebar>

      <GlobalSidebar
        isOpen={taskFormOpen}
        onClose={() => {
          if (!saving) {
            setTaskFormOpen(false);
            setEditingTask(null);
            setTaskPhaseId(null);
          }
        }}
      >
        <div className="sidebarFormStack">
          <h2 className="sidebarFormTitle">
            {editingTask ? 'Edit task' : 'Add task'}
          </h2>
          <p className="bankProviderConnectIntro">
            {editingTask
              ? 'Update the task details.'
              : 'Add a task to this project phase.'}
          </p>
          <ProjectTaskForm
            task={editingTask ?? undefined}
            saving={saving}
            error={error}
            submitLabel={editingTask ? 'Save changes' : 'Add task'}
            onSubmit={handleTaskSubmit}
            onCancel={() => {
              setTaskFormOpen(false);
              setEditingTask(null);
              setTaskPhaseId(null);
            }}
          />
        </div>
      </GlobalSidebar>

      {onBulkImport ? (
        <GlobalSidebar isOpen={importSidebarOpen} onClose={() => setImportSidebarOpen(false)}>
          <ProjectTextImportSidebar
            existingPhases={phases}
            onImport={handleBulkImport}
            onCancel={() => setImportSidebarOpen(false)}
          />
        </GlobalSidebar>
      ) : null}
    </>
  );
}
